// src/pages/Transports.js — Admin portal transport management
import React, { useState, useEffect } from 'react';
import { get, post, patch, del } from '../utils/api';
import { Spinner, Alert, Modal, Input, Select } from '../components/Shared';

const TYPE_OPTIONS = [
  { value:'courier',   label:'Courier' },
  { value:'logistics', label:'Logistics' },
  { value:'3pl',       label:'3PL / Own Vehicle' },
];

export default function Transports() {
  const [transports, setTransports] = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [showAdd,    setShowAdd]    = useState(false);
  const [msg,        setMsg]        = useState(null);
  const [form,       setForm]       = useState({ name:'', type:'courier' });
  const [saving,     setSaving]     = useState(false);
  const [delTransport, setDelTransport] = useState(null);

  async function load() {
    try {
      const data = await get('/api/transports?active_only=false');
      setTransports(data.transports || []);
    } finally { setLoading(false); }
  }

  useEffect(() => { load(); }, []);

  async function addTransport(e) {
    e.preventDefault();
    setSaving(true);
    try {
      await post('/api/transports', form);
      setMsg({ type:'success', text:`${form.name} added successfully` });
      setShowAdd(false);
      setForm({ name:'', type:'courier' });
      load();
    } catch (err) {
      setMsg({ type:'error', text: err.message });
    } finally { setSaving(false); }
  }

  async function toggleActive(t) {
    try {
      await patch(`/api/transports/${t.id}`, { is_active: !t.is_active });
      setMsg({ type:'success', text:`${t.name} ${t.is_active ? 'deactivated' : 'activated'}` });
      load();
    } catch (err) { setMsg({ type:'error', text: err.message }); }
  }

  async function deleteTransport(t) {
    try {
      await del(`/api/transports/${t.id}`);
      setMsg({ type:'success', text:`${t.name} deleted` });
      setDelTransport(null);
      load();
    } catch (err) {
      setMsg({ type:'error', text: err.message });
      setDelTransport(null);
    }
  }

  const TYPE_COLORS = {
    courier:   'bg-blue-100 text-blue-700',
    logistics: 'bg-purple-100 text-purple-700',
    '3pl':     'bg-green-100 text-green-700',
  };

  if (loading) return <div className="flex justify-center p-12"><Spinner size="lg" /></div>;

  return (
    <div className="p-6 max-w-[1600px]">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Transport Management</h1>
          <p className="text-sm text-gray-500 mt-1">
            {transports.filter(t => t.is_active).length} active courier and logistics companies
          </p>
        </div>
        <button
          onClick={() => setShowAdd(true)}
          className="px-4 py-2 bg-blue-900 text-white text-sm rounded-lg hover:bg-blue-800 font-semibold"
        >
          + Add Transport
        </button>
      </div>

      {msg && <div className="mb-4"><Alert type={msg.type} message={msg.text} onClose={() => setMsg(null)} /></div>}

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              {['Name','Type','Status','Actions'].map((h,i) => (
                <th key={i} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {transports.map((t, i) => (
              <tr key={t.id} className={`border-t ${!t.is_active ? 'opacity-50' : ''}`}>
                <td className="px-4 py-3 font-medium text-gray-800">{t.name}</td>
                <td className="px-4 py-3">
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${TYPE_COLORS[t.type] || 'bg-gray-100 text-gray-600'}`}>
                    {t.type}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${t.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                    {t.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex gap-2">
                    <button
                      onClick={() => toggleActive(t)}
                      className={`text-xs px-2 py-1 rounded font-medium ${t.is_active ? 'bg-amber-50 text-amber-600 hover:bg-amber-100' : 'bg-green-50 text-green-600 hover:bg-green-100'}`}
                    >
                      {t.is_active ? 'Deactivate' : 'Activate'}
                    </button>
                    <button
                      onClick={() => setDelTransport(t)}
                      className="text-xs px-2 py-1 rounded font-medium bg-red-50 text-red-600 hover:bg-red-100"
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {delTransport && (
        <Modal title="Delete Transport" onClose={() => setDelTransport(null)}>
          <div className="text-center py-4">
            <p className="text-5xl mb-4">⚠️</p>
            <p className="text-gray-800 font-semibold text-lg mb-2">Delete {delTransport.name}?</p>
            <p className="text-gray-500 text-sm mb-6">
              This permanently removes this transport from the system. Historical delivery records using this transport will still show the transport name.
            </p>
            <div className="flex gap-3">
              <button onClick={() => setDelTransport(null)}
                className="flex-1 py-3 border border-gray-300 rounded-xl text-sm font-semibold text-gray-600 hover:bg-gray-50">
                Cancel
              </button>
              <button onClick={() => deleteTransport(delTransport)}
                className="flex-1 py-3 bg-red-600 text-white rounded-xl text-sm font-bold hover:bg-red-500">
                Yes, Delete
              </button>
            </div>
          </div>
        </Modal>
      )}

      {showAdd && (
        <Modal title="Add Transport Company" onClose={() => setShowAdd(false)}>
          <form onSubmit={addTransport}>
            <Input
              label="Company Name"
              value={form.name}
              onChange={e => setForm({...form, name:e.target.value})}
              required
              placeholder="e.g. BlueDart, DTDC, Own Vehicle"
            />
            <Select
              label="Type"
              value={form.type}
              onChange={e => setForm({...form, type:e.target.value})}
              options={TYPE_OPTIONS}
            />
            <div className="flex gap-3 mt-2">
              <button type="button" onClick={() => setShowAdd(false)}
                className="flex-1 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50">
                Cancel
              </button>
              <button type="submit" disabled={saving}
                className="flex-1 py-2 bg-blue-900 text-white rounded-lg text-sm font-semibold hover:bg-blue-800 disabled:opacity-60">
                {saving ? 'Adding...' : 'Add Transport'}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
